import Component from '@glimmer/component'
import { action } from '@ember/object'
import { tracked } from '@glimmer/tracking'
import { service } from '@ember/service'
import Icon from 'customer-flows/components/ui-kit/icon'
import didUpdate from '@ember/render-modifiers/modifiers/did-update'

interface FlowProgressBarArgs {
  onBackClicked?: () => void
  showBackButton?: boolean
  showSteps?: boolean
  excludePages?: PageType[]
  done?: boolean
}

class ProgressTracker {
  @tracked lastKnownProgress = 0
}

// Singleton progress tracker so multiple instances can share the same progress
export const progress = new ProgressTracker()

export default class FlowProgressBar extends Component<FlowProgressBarArgs> {
  @service flowManager: FlowManager

  constructor(owner: unknown, args: FlowProgressBarArgs) {
    super(owner, args)

    requestAnimationFrame(() => {
      this.updateProgress()
    })
  }

  willDestroy() {
    super.willDestroy()
    progress.lastKnownProgress = 0
  }

  get showBackButton() {
    return this.args.showBackButton ?? true
  }

  get showSteps() {
    return this.args.showSteps ?? true
  }

  get totalPages() {
    if (!this.flowManager.activeFlow) {
      return 1
    }

    const excludePages = this.args.excludePages ?? []
    const pages = this.flowManager.activeFlow.pages.filter((page) => !excludePages.includes(page.type))

    return pages.length
  }

  get currentPage() {
    if (!this.flowManager.activeFlow) return 1

    const excludePages = this.args.excludePages ?? []
    const currentRouteName = this.flowManager.activeFlow.activePage?.type

    if (!currentRouteName) return 1

    // Find the index of the current page, filtering out excluded pages
    const pages = this.flowManager.activeFlow.pages.filter(
      (page) => !excludePages.includes(page.type)
    )

    const currentPageIndex = pages.findIndex((page) => page.type === currentRouteName)

    // If page not found or it's the first page, return 1
    return currentPageIndex >= 0 ? currentPageIndex + 1 : 1
  }

  @action
  onBackClicked() {
    if (this.args.onBackClicked) {
      this.args.onBackClicked()
    } else {
      this.flowManager.back()
    }
  }

  @action
  updateProgress() {
    if (this.totalPages <= 0) {
      progress.lastKnownProgress = this.args.done ? 100 : 0
      return
    }

    // Calculate inclusive progress scaled to 90%
    const rawProgress = (this.currentPage / this.totalPages) * 90

    progress.lastKnownProgress = this.args.done ? 100 : Math.min(rawProgress, 90)
  }

  <template>
    <div
      data-testid='ui-kit__progress-bar-container'
      class='flex w-full items-center gap-2.5 p-2.5 max-w-268'
      {{didUpdate this.updateProgress @done}}
    >
      {{#if this.showBackButton}}
        <il-button
          data-testid='ui-kit__progress-bar__back-button'
          {{! @glint-ignore: wait until we can't declare custom elements in glint }}
          onClick={{this.onBackClicked}}
          iconOnly={{true}}
          variant='ghost'
          small={{true}}
        >
          <Icon @src='back' />
        </il-button>
      {{/if}}
      <div data-testid='ui-kit__progress-bar' class='flex-1'>
        <il-progress-bar
          totalPages={{this.totalPages}}
          step={{this.currentPage}}
          progress={{progress.lastKnownProgress}}
          showSteps={{this.showSteps}}
        />
      </div>
    </div>
  </template>
}
